import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { apiUrl } from '#GameFrontend/services/api';

export type SupportedLanguages = 'de' | 'en' | 'ru';

interface TranslationResources {
	[key: string]: {
		translation: Record<string, string>;
	};
}

async function fetchWithRetry(url: string, options = {}, retries = 3, delay = 1000) {
	for (let i = 0; i < retries; i++) {
		try {
			const response = await fetch(url, options);
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			return await response.json();
		} catch (error) {
			if (i < retries - 1) {
				console.warn(`Fetch attempt ${i + 1} failed. Retrying in ${delay}ms...`);
				await new Promise((resolve) => setTimeout(resolve, delay));
			} else {
				console.error(`Fetch failed after ${retries} attempts.`);
				throw error;
			}
		}
	}
}

// Function to load translations for a specific language
export async function loadLanguageAsync(language: SupportedLanguages): Promise<void> {
	// Skip if language is already loaded
	if (i18n.hasResourceBundle(language, 'translation')) {
		return;
	}

	try {
		const response = await fetchWithRetry(apiUrl(`/lang/${language}`), {}, 10, 10000);
		i18n.addResourceBundle(language, 'translation', response, true, true);
	} catch (error) {
		console.error(`Failed to load translations for ${language}:`, error);
		throw error;
	}
}

// Initialize i18n with empty resources first
i18n.use(LanguageDetector)
	.use(initReactI18next)
	.init({
		debug: process.env.NODE_ENV === 'development',
		fallbackLng: 'en',
		supportedLngs: ['de', 'en', 'ru'],
		resources: {},
		detection: {
			order: ['localStorage', 'navigator'],
			caches: ['localStorage'],
		},
		interpolation: {
			escapeValue: false, // React already safes from xss
		},
		react: {
			useSuspense: true,
			transSupportBasicHtmlNodes: true,
			transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p', 'span'],
		},
	});

// Get browser language and map it to supported languages
const getBrowserLanguage = (): SupportedLanguages => {
	const languages = navigator.languages || [navigator.language];
	// Get the first language code and normalize it
	const language = languages[0]?.split('-')[0].toLowerCase();

	// Map to supported languages
	if (language && ['de', 'en', 'ru'].includes(language)) {
		return language as SupportedLanguages;
	}
	return 'en'; // Fallback to English
};

// Add language change handler
i18n.on('languageChanged', async (lng) => {
	try {
		await loadLanguageAsync(lng as SupportedLanguages);
	} catch (error) {
		console.error('Error loading language:', error);
		// If loading fails and it's not English, try to fall back to English
		if (lng !== 'en') {
			i18n.changeLanguage('en');
		}
	}
});

// Initial language setup
const language = getBrowserLanguage();
loadLanguageAsync(language)
	.then(async () => {
		// If the language is not English, load English as fallback
		if (language !== 'en') {
			try {
				await loadLanguageAsync('en');
			} catch (error) {
				console.error('Failed to fetch English fallback translations:', error);
			}
		}
		// Change language after resources are loaded
		i18n.changeLanguage(language);
	})
	.catch((error) => console.error('Initial language setup failed:', error));

export default i18n;
